import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import {
    FiHome,
    FiBriefcase,
    FiCheckSquare,
    FiClock,
    FiActivity,
    FiFileText,
    FiShield,
    FiLogOut,
    FiMenu,
    FiX,
    FiUser,
    FiDownload,
    FiTrash2,
    FiEye,
    FiCheckCircle,
    FiMail,
    FiPhone,
    FiCreditCard,
    FiList,
    FiXCircle
} from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { jsPDF } from "jspdf";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CandidateLayout = () => {
    const [profile, setProfile] = useState(null);
    const [appliedJobs, setAppliedJobs] = useState([]);
    const [allJobs, setAllJobs] = useState([]);
    const [savedJobs, setSavedJobs] = useState([]);
    const [personalDocs, setPersonalDocs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [logoUrl, setLogoUrl] = useState("https://placehold.co/200x60/6366f1/ffffff?text=Timely+Health");

    // Shared state for Document Viewer Modal
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeDocTab, setActiveDocTab] = useState("offer");

    const navigate = useNavigate();
    const location = useLocation();
    const isJobDetailsPage = location.pathname.startsWith("/jobs/");
    const [showLogoutDialog, setShowLogoutDialog] = useState(false);

    useEffect(() => {
        fetchSettings();
        fetchDashboardData();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/admin/settings`);
            if (res.data.success && res.data.settings.companyLogo) {
                const logo = res.data.settings.companyLogo;
                const DYNAMIC_DOMAIN = API_BASE_URL.replace("/api", "");
                setLogoUrl(logo.startsWith('http') ? logo : `${DYNAMIC_DOMAIN}${logo}`);
            }
        } catch (err) {
            console.error("Failed to fetch settings:", err);
        }
    };

    const fetchDashboardData = async () => {
        const token = localStorage.getItem("candidateToken");
        const employeeId = localStorage.getItem("employeeId");
        const isPublicPage = location.pathname.startsWith("/jobs");

        if (!token && !employeeId) {
            if (!isPublicPage) {
                navigate("/candidate-login");
            }
            setLoading(false);
            return;
        }

        try {
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const profileRes = await axios.get(`${API_BASE_URL}/candidate/profile`, { headers });
            let profileData = profileRes.data;

            let applications = [];
            try {
                const appsRes = await axios.get(`${API_BASE_URL}/candidate/applications`, { headers });
                applications = Array.isArray(appsRes.data) ? appsRes.data : [];
            } catch (appErr) {
                try {
                    const allAppsRes = await axios.get(`${API_BASE_URL}/applications/all`);
                    const allApps = allAppsRes.data?.applications || [];
                    const candidateEmail = profileData?.email || "";
                    applications = allApps.filter(app =>
                        app.email?.toLowerCase() === candidateEmail.toLowerCase()
                    );
                } catch (fallbackErr) {
                    console.error("Both application fetch attempts failed:", fallbackErr);
                }
            }
            setAppliedJobs(applications);
            setProfile(profileData);

            const docsRes = await axios.get(`${API_BASE_URL}/candidate/documents`, { headers });
            if (docsRes.data && docsRes.data.success) {
                setPersonalDocs(docsRes.data.data);
            }

            const allJobsRes = await axios.get(`${API_BASE_URL}/jobs/all`);
            setAllJobs(allJobsRes.data.jobPosts || []);

            const savedRes = await axios.get(`${API_BASE_URL}/candidate/saved-jobs`, { headers });
            if (savedRes.data && savedRes.data.success) {
                setSavedJobs(savedRes.data.savedJobs || []);
            }

        } catch (err) {
            console.error("Fetch dashboard error:", err);
            if (err.response?.status === 401 && !localStorage.getItem("employeeId") && !isPublicPage) {
                navigate("/candidate-login");
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleSaveJob = async (jobId) => {
        try {
            const token = localStorage.getItem("candidateToken");
            if (!token) {
                navigate("/candidate-login");
                return;
            }
            const res = await axios.post(`${API_BASE_URL}/candidate/saved-jobs/toggle`, { jobId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                const updatedRes = await axios.get(`${API_BASE_URL}/candidate/saved-jobs`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (updatedRes.data.success) {
                    setSavedJobs(updatedRes.data.savedJobs);
                    toast.success(res.data.isSaved ? "Job saved successfully!" : "Job removed from saved list");
                }
            }
        } catch (err) {
            console.error("Toggle save error:", err);
            toast.error("Failed to update saved job status");
        }
    };

    const handleEditToggle = () => {
        if (!isEditingProfile) {
            setEditForm({
                name: profile?.name || "",
                phone: profile?.phone || profile?.mobile || "",
                address: profile?.address || "",
                qualification: profile?.qualification || "",
                percentage: profile?.percentage || "",
                passingYear: profile?.passingYear || "",
                experience: profile?.experience || "",
                currentCompany: profile?.currentCompany || profile?.companyName || "",
                currentCTC: profile?.currentCTC || profile?.salary || "",
                expectedCTC: profile?.expectedCTC || profile?.expectedSalary || "",
                skills: profile?.skills || ""
            });
        }
        setIsEditingProfile(!isEditingProfile);
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setIsSavingProfile(true);
        try {
            const token = localStorage.getItem("candidateToken");
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.put(`${API_BASE_URL}/candidate/profile`, editForm, { headers });
            if (res.data) {
                setProfile(prev => ({ ...prev, ...res.data.candidate }));
                setIsEditingProfile(false);
                toast.success("Profile updated successfully!");
            }
        } catch (err) {
            console.error("Update profile error:", err);
            toast.error(err.response?.data?.message || "Failed to update profile.");
        } finally {
            setIsSavingProfile(false);
        }
    };

    const logout = () => {
        setShowLogoutDialog(true);
    };

    const confirmLogout = () => {
        localStorage.removeItem("candidateToken");
        localStorage.removeItem("candidateId");
        setShowLogoutDialog(false);
        navigate("/candidate-login");
    };

    const cancelLogout = () => {
        setShowLogoutDialog(false);
    };

    const formatDocumentUrl = (filePath) => {
        if (!filePath) return "";
        const relativePath = filePath.includes("uploads")
            ? "uploads/" + filePath.split(/uploads[\\/]/).pop().replace(/\\/g, "/")
            : filePath.replace(/\\/g, "/");
        return `${API_BASE_URL.replace("/api", "")}/${relativePath}`;
    };

    const getBase64ImageFromUrl = (imgUrl) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                resolve({ dataUrl: canvas.toDataURL("image/png"), width: img.width, height: img.height });
            };
            img.onerror = () => resolve(null);
            img.src = imgUrl;
        });
    };

    const handleDownloadOffer = async (appOrLetter) => {
        if (!appOrLetter) return;
        const content = appOrLetter.content || appOrLetter.offerLetter || appOrLetter.resignationLetter;
        let type = appOrLetter.resolvedDocType || appOrLetter.documentType || (appOrLetter.resignationLetter ? "Resignation" : "Offer");
        if (type === "Offer") type = "Offer_Letter";
        const sentAt = appOrLetter.sentAt || appOrLetter.offerSentAt || appOrLetter.resignationSentAt || appOrLetter.updatedAt;

        if (appOrLetter.adminAttachment && !content) {
            window.open(formatDocumentUrl(appOrLetter.adminAttachment), "_blank");
            return;
        }

        if (content) {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const margin = 20;
            const contentWidth = pageWidth - (2 * margin);

            // --- Letterhead Design ---
            doc.setFillColor(248, 250, 252);
            doc.rect(0, 0, pageWidth, 45, 'F');

            const logoBase64 = await getBase64ImageFromUrl(logoUrl);
            if (logoBase64 && logoBase64.dataUrl) {
                const ratio = logoBase64.width / logoBase64.height;
                const imgHeight = 12;
                const imgWidth = imgHeight * ratio;
                doc.addImage(logoBase64.dataUrl, "PNG", margin, 15, imgWidth, imgHeight);
            } else {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(22);
                doc.setTextColor(30, 41, 59);
                doc.text("TIMELY HEALTH", margin, 25);
            }

            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(100, 116, 139);
            doc.text("", margin, 32);

            const headerTitle = (type.replace('_', ' ') || "OFFICIAL").toUpperCase();
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(63, 81, 181);
            doc.text(`${headerTitle} LETTER`, pageWidth - margin, 25, { align: "right" });

            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(148, 163, 184);
            doc.text(`REF: ${(appOrLetter._id || "REF").toString().slice(-10).toUpperCase()}`, pageWidth - margin, 32, { align: "right" });

            doc.setDrawColor(226, 232, 240);
            doc.setLineWidth(0.5);
            doc.line(margin, 45, pageWidth - margin, 45);

            let currentY = 60;
            doc.setFontSize(10);
            doc.setTextColor(71, 85, 105);
            doc.text(`Issued On: ${new Date(sentAt).toLocaleDateString('en-GB')}`, pageWidth - margin, currentY, { align: "right" });

            doc.setFont("helvetica", "bold");
            doc.setTextColor(30, 41, 59);
            doc.text("TO:", margin, currentY);
            doc.setFont("helvetica", "normal");
            currentY += 6;
            const fullName = profile?.name || (appOrLetter.firstName ? `${appOrLetter.firstName} ${appOrLetter.lastName}` : "Valued Candidate");
            doc.text(fullName, margin, currentY);
            currentY += 5;
            doc.setFontSize(9);
            doc.setTextColor(100, 116, 139);
            doc.text(`${appOrLetter.jobId?.role || "Associate"}`, margin, currentY);

            currentY += 15;

            // Content Sanitization
            const sanitizedContent = content
                .replace(/\*\*/g, "")
                .replace(/\*/g, "")
                .replace(/#{1,6}\s?/g, "")
                .replace(/~~/g, "");

            doc.setFontSize(11);
            doc.setTextColor(51, 65, 85);
            doc.setFont("helvetica", "normal");

            const lines = doc.splitTextToSize(sanitizedContent, contentWidth);

            lines.forEach(line => {
                if (currentY > pageHeight - 50) {
                    doc.addPage();
                    currentY = 30;
                }
                doc.text(line, margin, currentY);
                currentY += 6.5;
            });

            currentY += 20;
            if (currentY > pageHeight - 60) {
                doc.addPage();
                currentY = 40;
            }

            doc.setFont("helvetica", "bold");
            doc.text("Sincerely,", margin, currentY);
            currentY += 15;
            doc.text("__________________________", margin, currentY);
            currentY += 5;
            doc.setFontSize(9);
            doc.text("Authorized Signatory", margin, currentY);
            doc.setFont("helvetica", "normal");
            doc.text("Recruitment Division | Timely Health Projects", margin, currentY + 5);

            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(148, 163, 184);
                doc.setDrawColor(226, 232, 240);
                doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
                doc.text(`TIMELY HEALTH PROJECTS • CONFIDENTIAL • PAGE ${i} OF ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: "center" });
            }

            const firstName = fullName.split(' ')[0];
            const fileName = `${type}_${firstName}_${new Date(sentAt).getFullYear()}.pdf`;
            doc.save(fileName);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.user-dropdown-container')) {
                setIsUserDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-white font-sans">
            <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-slate-100 border-t-indigo-600 rounded-full animate-spin"></div>
                <p className="text-slate-500 text-xs font-bold uppercase tracking-widest animate-pulse">Synchronizing Portal...</p>
            </div>
        </div>
    );

    const navItems = [
        { path: "/candidate-dashboard", label: "Home", icon: FiHome },
        { path: "/all-jobs", label: "All Jobs", icon: FiBriefcase },
        { path: "/jobs", label: "Short Jobs", icon: FiClock },
        { path: "/applied-jobs", label: "Applied Jobs", icon: FiCheckSquare },
        { path: "/saved-jobs", label: "Saved Jobs", icon: FiActivity },
        // { path: "/my-jobs", label: "My Experience", icon: FiActivity },
        { path: "/interview", label: "Interviews", icon: FiClock },
        { path: "/letters", label: "Letters", icon: FiMail },
        { path: "/candidate-personal-documents", label: "Profile", icon: FiFileText },
        { path: "/my-wallet", label: "Wallet", icon: FiCreditCard },
        // { path: "/medical-certificate", label: "Health Certificate", icon: FiActivity },
    ];
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col font-sans select-none overflow-x-hidden">
            {/* TOP NAVIGATION BAR */}
            {!isJobDetailsPage && (
                <nav className="sticky top-0 z-[130] w-full bg-white border-b border-gray-200 shadow-sm px-4 lg:px-6">
                    <div className="w-full h-16 flex items-center justify-between gap-8">

                        {/* Brand / Logo */}
                        <div
                            className="flex items-center gap-3 cursor-pointer group"
                            onClick={() => navigate(profile ? "/candidate-dashboard" : "/all-jobs")}
                        >
                            <div className="w-1.5 h-8 bg-indigo-500 rounded-full mr-1"></div>
                            <div className="flex flex-col">
                                <span className="text-sm font-black tracking-tight leading-none uppercase text-gray-900">INGRAIN</span>
                                <span className="text-[10px] font-bold tracking-[0.2em] text-gray-500 mt-1 uppercase">HIRE</span>
                            </div>
                        </div>

                        {/* Desktop Navigation Links */}
                        <div className="hidden xl:flex items-center gap-1">
                            {navItems.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        className={`px-4 py-2 rounded-xl text-[12px] font-black uppercase tracking-wider transition-all duration-300 relative group/link ${isActive
                                            ? 'text-indigo-600 bg-indigo-50'
                                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                            }`}
                                    >
                                        {item.label}
                                        {isActive && (
                                            <motion.div
                                                layoutId="nav-active"
                                                className="absolute bottom-0 left-4 right-4 h-0.5 bg-indigo-600 rounded-full"
                                            />
                                        )}
                                    </NavLink>
                                );
                            })}
                        </div>
                        {/* Right Section: Profile & Mobile Toggle */}
                        <div className="flex items-center gap-4">
                            {profile ? (
                                <div className="relative user-dropdown-container" onMouseEnter={() => setIsUserDropdownOpen(true)} onMouseLeave={() => setIsUserDropdownOpen(false)}>
                                    <div className="flex items-center gap-3 cursor-pointer p-1.5 rounded-2xl transition-all duration-300 hover:bg-gray-100 group">
                                        <div className="text-right hidden sm:block">
                                            <p className="text-[11px] font-black text-gray-900 leading-none uppercase tracking-wide">{profile?.name?.split(' ')[0] || "Candidate"}</p>
                                            <p className="text-[8px] font-bold text-gray-500 mt-1 uppercase tracking-widest">Active Profile</p>
                                        </div>
                                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center font-black text-sm shadow-sm ring-2 ring-indigo-100 transition-all group-hover:scale-105">
                                            {profile?.name?.charAt(0) || "C"}
                                        </div>
                                    </div>

                                    {/* HOVER DROPDOWN (Integrated Profile Menu) */}
                                    <AnimatePresence>
                                        {isUserDropdownOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                className="absolute top-[115%] right-0 w-80 bg-white border border-gray-100 rounded-[2.5rem] shadow-[0_20px_50px_rgba(79,70,229,0.15)] p-8 z-[160]"
                                            >
                                                <div className="flex flex-col items-center mb-8">
                                                    <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-600 to-violet-700 flex items-center justify-center text-white text-3xl font-bold shadow-xl ring-8 ring-indigo-50/50 mb-5 relative group">
                                                        {profile?.name?.charAt(0) || "C"}
                                                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-emerald-500 border-4 border-white rounded-full"></div>
                                                    </div>
                                                    <h3 className="text-sm font-bold text-gray-900 tracking-tight text-center">{profile?.name}</h3>
                                                    <span className="mt-2 px-3 py-1 bg-indigo-50 text-indigo-600 text-[10px] font-bold uppercase tracking-widest rounded-full">Verified Candidate</span>
                                                </div>

                                                <div className="space-y-4 mb-8">
                                                    <div className="bg-gray-50/80 p-4 rounded-2xl border border-gray-100/50 transition-all hover:bg-white hover:shadow-md group">
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                                                            <FiMail className="text-indigo-500" strokeWidth={3} /> Primary Email
                                                        </p>
                                                        <p className="text-xs font-bold text-gray-800 break-all truncate">{profile?.email}</p>
                                                    </div>
                                                    <div className="bg-gray-50/80 p-4 rounded-2xl border border-gray-100/50 transition-all hover:bg-white hover:shadow-md group">
                                                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                                                            <FiUser className="text-indigo-500" strokeWidth={3} /> Professional ID
                                                        </p>
                                                        <p className="text-xs font-bold text-gray-800">CAND-{(profile?._id || "REF").slice(-6).toUpperCase()}</p>
                                                    </div>
                                                </div>

                                                <div className="space-y-3">
                                                    <button
                                                        onClick={() => { setIsProfileModalOpen(true); setIsUserDropdownOpen(false); }}
                                                        className="w-full py-4 bg-indigo-600 text-white rounded-2xl text-[10px] font-bold uppercase tracking-[0.15em] shadow-lg shadow-indigo-200 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all active:scale-95"
                                                    >
                                                        Edit Portal Records
                                                    </button>
                                                    <button
                                                        onClick={logout}
                                                        className="w-full py-4 bg-white text-rose-500 border border-rose-100 rounded-2xl text-[10px] font-bold uppercase tracking-[0.15em] hover:bg-rose-50 transition-all active:scale-95 flex items-center justify-center gap-2"
                                                    >
                                                        Sign out <FiLogOut size={14} strokeWidth={3} />
                                                    </button>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            const currentJobId = location.pathname.startsWith("/jobs/") ? location.pathname.split("/").pop() : "";
                                            navigate(currentJobId ? `/candidate-login/${currentJobId}` : "/candidate-login");
                                        }}
                                        className="px-5 py-2 text-[11px] font-black text-gray-600 uppercase tracking-widest hover:bg-gray-100 rounded-xl transition-all"
                                    >
                                        Log In
                                    </button>
                                    <button
                                        onClick={() => {
                                            const currentJobId = location.pathname.startsWith("/jobs/") ? location.pathname.split("/").pop() : "";
                                            navigate(currentJobId ? `/candidate-register/${currentJobId}` : "/candidate-register");
                                        }}
                                        className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-[11px] font-black uppercase tracking-widest rounded-xl shadow-sm hover:shadow-md transition-all"
                                    >
                                        Join Now
                                    </button>
                                </div>
                            )}

                            {/* Mobile Menu Toggle */}
                            <button
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="xl:hidden p-2.5 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all border border-gray-200"
                            >
                                {isMobileMenuOpen ? <FiX size={22} strokeWidth={3} /> : <FiMenu size={22} strokeWidth={3} />}
                            </button>
                        </div>
                    </div>

                    {/* MOBILE MENU (Expanding) */}
                    <AnimatePresence>
                        {isMobileMenuOpen && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="xl:hidden overflow-hidden bg-[#1D4ED8] border-t border-white/10"
                            >
                                <div className="flex flex-col py-4 px-2 space-y-1">
                                    {navItems.map((item) => (
                                        <NavLink
                                            key={item.path}
                                            to={item.path}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                            className={({ isActive }) => `flex items-center gap-4 px-6 py-4 rounded-2xl text-[12px] font-black uppercase tracking-wider transition-all ${isActive
                                                ? 'bg-white/10 text-white'
                                                : 'text-blue-100/70 hover:bg-white/5 hover:text-white'
                                                }`}
                                        >
                                            {item.label}
                                        </NavLink>
                                    ))}
                                    {profile ? (
                                        <button
                                            onClick={logout}
                                            className="flex items-center gap-4 px-6 py-4 rounded-2xl text-[12px] font-black uppercase tracking-wider text-rose-300 hover:bg-rose-500/10 transition-all border-t border-white/5 mt-2"
                                        >
                                            Sign Out
                                        </button>
                                    ) : (
                                        <div className="p-4 grid grid-cols-2 gap-3 border-t border-white/5 mt-2">
                                            <button
                                                onClick={() => {
                                                    const currentJobId = location.pathname.startsWith("/jobs/") ? location.pathname.split("/").pop() : "";
                                                    navigate(currentJobId ? `/candidate-login/${currentJobId}` : "/candidate-login");
                                                    setIsMobileMenuOpen(false);
                                                }}
                                                className="w-full py-4 text-center text-[11px] font-black text-white uppercase tracking-widest border border-white/20 rounded-2xl"
                                            >
                                                Log In
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const currentJobId = location.pathname.startsWith("/jobs/") ? location.pathname.split("/").pop() : "";
                                                    navigate(currentJobId ? `/candidate-register/${currentJobId}` : "/candidate-register");
                                                    setIsMobileMenuOpen(false);
                                                    }}
                                                className="w-full py-4 text-center text-[11px] font-black text-[#1D4ED8] bg-white uppercase tracking-widest rounded-2xl shadow-xl"
                                            >
                                                Join Now
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </nav>
            )}

            {/* MAIN PORTAL AREA */}
            <main className="flex-1 overflow-x-hidden overflow-y-auto no-scrollbar scroll-smooth">
                {/* Content Area (Full Width) */}
                <div className={`w-full h-full ${isJobDetailsPage ? 'p-0' : 'p-4 md:p-6 lg:px-6'}`}>
                    {/* Content Entry with Animation */}
                    <motion.div
                        key={location.pathname}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className="w-full h-full"
                    >
                        <Outlet context={{
                            profile, appliedJobs, allJobs, personalDocs, savedJobs, setSavedJobs,
                            toggleSaveJob,
                            searchQuery, setSearchQuery,
                            fetchDashboardData, formatDocumentUrl,
                            handleDownloadOffer, setIsModalOpen,
                            setSelectedOffer, setActiveDocTab, logoUrl
                        }} />
                    </motion.div>
                </div>
            </main>

            {/* PROFILE MODAL - Re-styled for Exact Match */}
            {isProfileModalOpen && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="bg-white/30 backdrop-blur-xl max-w-2xl w-full rounded-[2.5rem] shadow-2xl overflow-hidden relative border border-white/20 flex flex-col max-h-[90vh]"
                    >
                        {/* Modal Header */}
                        <div className="px-8 py-8 bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 flex items-center justify-between shadow-xl text-white shrink-0 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                                <div className="absolute top-[-50%] left-[-10%] w-[40%] h-[200%] bg-white/20 rotate-12 blur-3xl"></div>
                            </div>

                            <div className="flex items-center gap-6 relative z-10">
                                <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-xl text-white flex items-center justify-center text-2xl font-bold border border-white/20 shadow-inner">
                                    {profile?.name?.charAt(0) || "C"}
                                </div>
                                <div className="min-w-0">
                                    <h1 className="text-xl font-bold text-white leading-tight truncate tracking-tight">{profile?.name}</h1>
                                    <div className="flex items-center gap-2 mt-1.5 opacity-80">
                                        <FiMail size={12} />
                                        <p className="text-[10px] font-bold uppercase tracking-widest truncate">{profile?.email}</p>
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => setIsProfileModalOpen(false)} className="p-2 text-white/50 hover:text-white transition-colors relative z-10 bg-white/10 rounded-xl">
                                <FiX size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-8 overflow-y-auto bg-gray-50/30 no-scrollbar flex-1">
                            {isEditingProfile ? (
                                <form id="profile-edit-form" onSubmit={handleUpdateProfile} className="space-y-8">
                                    <section className="space-y-6">
                                        <div className="flex items-center justify-between px-2">
                                            <div className="flex items-center gap-3">
                                                <div className="w-1.5 h-6 bg-indigo-600 rounded-full shadow-[0_0_10px_rgba(79,70,229,0.3)]"></div>
                                                <h3 className="text-[11px] font-bold text-gray-900 uppercase tracking-[0.2em]">Identity & Contact</h3>
                                            </div>
                                            <span className="text-[9px] font-bold text-indigo-500/50 uppercase tracking-widest">Core Profile</span>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-1">
                                            {[
                                                { label: "Legal Full Name", key: "name", icon: FiUser, placeholder: "e.g. John Doe" },
                                                { label: "Contact Phone", key: "phone", icon: FiPhone, placeholder: "+1 (555) 000-0000" },
                                            ].map(field => (
                                                <div key={field.key} className="space-y-2.5">
                                                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">{field.label}</label>
                                                    <div className="relative group/input">
                                                        <div className="absolute left-0 top-0 w-11 h-full flex items-center justify-center text-gray-400 group-focus-within/input:text-indigo-600 transition-colors z-10">
                                                            <field.icon size={14} />
                                                        </div>
                                                        <input
                                                            type="text"
                                                            placeholder={field.placeholder}
                                                            className="w-full bg-white border border-gray-100 rounded-2xl pl-11 pr-4 py-4 text-xs font-bold text-gray-800 placeholder:text-gray-300 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 outline-none transition-all shadow-[0_2px_10px_rgba(0,0,0,0.02)]"
                                                            value={editForm[field.key]}
                                                            onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                                                            required={field.key === "name"}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="sm:col-span-2 space-y-2.5">
                                                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">Geographic Location</label>
                                                <div className="relative group/input">
                                                    <div className="absolute left-4 top-5 text-gray-400 group-focus-within/input:text-indigo-600 transition-colors z-10">
                                                        <FiHome size={14} />
                                                    </div>
                                                    <textarea
                                                        placeholder="Residential Address"
                                                        className="w-full bg-white border border-gray-100 rounded-2xl pl-11 pr-5 py-4 text-xs font-bold text-gray-800 placeholder:text-gray-300 focus:ring-4 focus:ring-indigo-500/5 focus:border-indigo-500 outline-none transition-all shadow-[0_2px_10px_rgba(0,0,0,0.02)] resize-none h-28"
                                                        value={editForm.address}
                                                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </section>

                                    <section className="space-y-6">
                                        <div className="flex items-center justify-between px-2">
                                            <div className="flex items-center gap-3">
                                                <div className="w-1.5 h-6 bg-emerald-500 rounded-full shadow-[0_0_10px_rgba(16,185,129,0.3)]"></div>
                                                <h3 className="text-[11px] font-bold text-gray-900 uppercase tracking-[0.2em]">Professional DNA</h3>
                                            </div>
                                            <span className="text-[9px] font-bold text-emerald-500/50 uppercase tracking-widest">Career Data</span>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 p-1">
                                            {[
                                                { label: "Highest Qualification", key: "qualification", icon: FiBriefcase },
                                                { label: "Expected Remuneration", key: "expectedCTC", icon: FiActivity },
                                                { label: "Current Organization", key: "currentCompany", icon: FiUser },
                                                { label: "Total Tenure (Years)", key: "experience", icon: FiClock },
                                            ].map(field => (
                                                <div key={field.key} className="space-y-2.5">
                                                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">{field.label}</label>
                                                    <div className="relative group/input">
                                                        <div className="absolute left-0 top-0 w-11 h-full flex items-center justify-center text-gray-400 group-focus-within/input:text-emerald-500 transition-colors z-10">
                                                            <field.icon size={13} />
                                                        </div>
                                                        <input
                                                            type="text"
                                                            className="w-full bg-white border border-gray-100 rounded-2xl pl-11 pr-5 py-4 text-xs font-bold text-gray-800 focus:ring-4 focus:ring-emerald-500/5 focus:border-emerald-500 outline-none transition-all shadow-[0_2px_10px_rgba(0,0,0,0.02)]"
                                                            value={editForm[field.key]}
                                                            onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                </form>
                            ) : (
                                <div className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm space-y-5">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-500 flex items-center justify-center shadow-inner"><FiUser size={14} /></div>
                                                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Connect Points</h3>
                                            </div>
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-4 group">
                                                    <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-500 transition-colors"><FiPhone size={14} /></div>
                                                    <div>
                                                        <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Phone</p>
                                                        <p className="text-xs font-bold text-gray-800">{profile?.phone || profile?.mobile || "Not Connected"}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4 group">
                                                    <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-indigo-50 group-hover:text-indigo-500 transition-colors"><FiHome size={14} /></div>
                                                    <div>
                                                        <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Address</p>
                                                        <p className="text-xs font-bold text-gray-800 truncate max-w-[180px]">{profile?.address || "N/A"}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm space-y-5">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="w-8 h-8 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center shadow-inner"><FiBriefcase size={14} /></div>
                                                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Professional DNA</h3>
                                            </div>
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-4 group">
                                                    <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-emerald-50 group-hover:text-emerald-500 transition-colors"><FiActivity size={14} /></div>
                                                    <div>
                                                        <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Org</p>
                                                        <p className="text-xs font-bold text-gray-800 truncate max-w-[180px]">{profile?.currentCompany || profile?.companyName || "N/A"}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4 group">
                                                    <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-emerald-50 group-hover:text-emerald-500 transition-colors"><FiClock size={14} /></div>
                                                    <div>
                                                        <p className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">Experience</p>
                                                        <p className="text-xs font-bold text-gray-800">{profile?.experience || "0"} Years</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-br from-gray-900 to-indigo-950 p-6 rounded-[2rem] border border-gray-800 shadow-2xl flex items-center justify-between group relative overflow-hidden">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-[50px] rounded-full pointer-events-none"></div>
                                        <div className="flex items-center gap-5 relative z-10">
                                            <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center text-rose-400 shadow-inner border border-white/5">
                                                <FiFileText size={28} />
                                            </div>
                                            <div>
                                                <span className="text-[9px] font-bold text-indigo-300 uppercase tracking-[0.2em]">Validated Profile</span>
                                                <h3 className="text-sm font-bold text-white tracking-tight mt-0.5 uppercase">Curriculum Vitae</h3>
                                            </div>
                                        </div>
                                        {profile?.resume ? (
                                            <button onClick={() => window.open(formatDocumentUrl(profile.resume), "_blank")} className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all hover:bg-indigo-500 hover:shadow-[0_0_20px_rgba(79,70,229,0.4)] active:scale-95 relative z-10">Access PDF</button>
                                        ) : (
                                            <span className="text-[10px] font-bold text-gray-500 italic relative z-10">Upload Pending</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-8 py-6 bg-white border-t border-gray-100 flex justify-end gap-4 shrink-0 shadow-[0_-10px_20px_rgba(0,0,0,0.02)]">
                            {isEditingProfile ? (
                                <>
                                    <button onClick={() => setIsEditingProfile(false)} className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">Discard</button>
                                    <button form="profile-edit-form" type="submit" disabled={isSavingProfile} className="px-10 py-3 bg-indigo-600 text-white rounded-2xl text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95">
                                        {isSavingProfile ? "Synchronizing..." : "Update Vault"}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button onClick={() => setIsProfileModalOpen(false)} className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors">Dismiss</button>
                                    <button onClick={handleEditToggle} className="px-10 py-3 bg-gray-900 text-white rounded-2xl text-[10px] font-bold uppercase tracking-widest shadow-xl hover:bg-black transition-all active:scale-95">Modify Records</button>
                                </>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}

            {/* DOCUMENT VIEWER MODAL - Re-styled for Exact Match */}
            {isModalOpen && selectedOffer && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 30 }}
                        className="bg-white w-full max-w-6xl h-[90vh] rounded-[2.5rem] shadow-[0_30px_100px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden border border-white/20 relative"
                    >
                        {/* Header Section */}
                        <div className="px-8 py-6 bg-gradient-to-r from-green-500 to-blue-600 flex justify-between items-center text-white shrink-0 relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                                <div className="absolute top-[-50%] left-[-10%] w-[40%] h-[200%] bg-white/20 rotate-12 blur-3xl"></div>
                            </div>

                            <div className="flex items-center gap-5 relative z-10">
                                <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center text-white border border-white/10 shadow-inner">
                                    <FiFileText size={28} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[9px] font-black text-white/80 uppercase tracking-[0.2em] mb-1 block">OFFICIAL RECORD</span>
                                    <h2 className="text-sm font-black text-white tracking-tight uppercase leading-none">
                                        {activeDocTab === "agreement" ? "Agreement & Documentation" : `${(selectedOffer.resolvedDocType || selectedOffer.documentType || "Official").toUpperCase()} LETTER`}
                                    </h2>
                                    <div className="text-[10px] font-bold text-white/60 uppercase tracking-widest mt-1.5 flex items-center gap-2">
                                        <FiBriefcase size={10} /> {selectedOffer.jobId?.role || selectedOffer.role || "Corporate Position"}
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 relative z-10">
                                {(selectedOffer.adminAttachment || selectedOffer.offerLetter || selectedOffer.resolvedContent) && (
                                    <button
                                        onClick={() => handleDownloadOffer(selectedOffer)}
                                        className="hidden sm:flex items-center gap-2 px-6 py-3 bg-white text-blue-600 border border-transparent rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-gray-50 transition-all active:scale-95"
                                    >
                                        <FiDownload size={12} /> DOWNLOAD PDF
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-gray-400 hover:text-white hover:bg-rose-500/20 transition-all active:scale-95 border border-white/5"
                                >
                                    <FiX size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div className="flex-1 overflow-hidden bg-gray-100/50 p-4 md:p-8 flex flex-col items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="w-full max-w-4xl h-full bg-white rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.05)] border border-gray-100 p-8 md:p-16 overflow-y-auto no-scrollbar font-sans text-sm leading-relaxed text-gray-800 relative"
                            >
                                {/* Decorative elements for the "paper" look */}
                                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-b from-gray-50 to-transparent"></div>

                                {(selectedOffer.resolvedContent || selectedOffer.offerLetter || selectedOffer.resignationLetter || selectedOffer.adminAgreements) && (
                                    <div className="whitespace-pre-wrap relative z-10 mb-8">
                                        {/* Mock Letterhead for content letters */}
                                        <div className="mb-12 border-b border-gray-100 pb-8 flex justify-between items-start">
                                            <div>
                                                <img src={logoUrl} alt="Logo" className="h-10 object-contain mb-1" />
                                                <div className="text-[9px] font-bold text-blue-500 uppercase tracking-[0.3em] mt-1">Global Recruitment Group</div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Document Hash</p>
                                                <p className="text-xs font-black text-gray-900 mt-1 uppercase">#{(selectedOffer._uid || selectedOffer._id || "REF").toString().slice(-8).toUpperCase()}</p>
                                            </div>
                                        </div>
                                        <div className="text-gray-700 font-medium leading-[1.8] tracking-tight">
                                            {selectedOffer.resolvedContent || selectedOffer.offerLetter || selectedOffer.resignationLetter || selectedOffer.adminAgreements}
                                        </div>
                                    </div>
                                )}

                                {(selectedOffer.attachmentPath || (selectedOffer.adminAttachment && !selectedOffer.resolvedContent)) ? (
                                    <div className={`w-full ${selectedOffer.resolvedContent ? 'mt-12 pt-12 border-t border-dashed border-gray-200' : 'h-full'}`}>
                                        {selectedOffer.resolvedContent && (
                                            <div className="flex items-center justify-between mb-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center shadow-inner"><FiFileText size={16} /></div>
                                                    <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">Associated Attachment</h3>
                                                </div>
                                                <button
                                                    onClick={() => window.open(formatDocumentUrl(selectedOffer.attachmentPath || selectedOffer.adminAttachment), "_blank")}
                                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl text-[9px] font-bold uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all active:scale-95 shadow-sm border border-indigo-100"
                                                >
                                                    <FiDownload size={12} /> Download / View Full File
                                                </button>
                                            </div>
                                        )}
                                        <div className={selectedOffer.resolvedContent ? "h-[500px] w-full" : "h-full w-full"}>
                                            <iframe
                                                src={formatDocumentUrl(selectedOffer.attachmentPath || selectedOffer.adminAttachment)}
                                                title="Document Viewer"
                                                className="w-full h-full border-0 rounded-2xl shadow-inner bg-gray-50"
                                            />
                                        </div>
                                    </div>
                                ) : (!selectedOffer.resolvedContent && !selectedOffer.offerLetter && !selectedOffer.adminAgreements && !selectedOffer.resignationLetter) && (
                                    <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                                        <div className="w-16 h-16 rounded-3xl bg-gray-50 flex items-center justify-center text-gray-300">
                                            <FiFileText size={32} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-400">Vault Content Empty</p>
                                            <p className="text-[10px] font-medium text-gray-300 uppercase tracking-widest mt-1">No data available for this record</p>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        </div>

                        {/* Footer Section */}
                        <div className="px-10 py-8 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-6 bg-white shrink-0 relative z-10">
                            <div className="flex flex-col">
                                <span className="text-[9px] font-black text-gray-300 uppercase tracking-[0.2em] mb-1">Authenticated Record</span>
                                <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest flex items-center gap-2">
                                    <FiShield className="text-emerald-500" /> TH-VERIFIED-{(selectedOffer._id || "REF").slice(-12).toUpperCase()}
                                </span>
                            </div>

                            <div className="flex gap-4 w-full sm:w-auto">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 sm:flex-none px-8 py-4 rounded-2xl bg-gray-50 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all active:scale-95"
                                >
                                    Dismiss Viewer
                                </button>
                                <button className="flex-1 sm:flex-none px-12 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-blue-100 hover:scale-[1.02] transition-all active:scale-95">
                                    Acknowledge Document
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Logout Confirmation Dialog */}
            {showLogoutDialog && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4 transform transition-all">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <FiLogOut className="text-red-600 text-xl" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">Confirm Logout</h3>
                        </div>
                        <p className="text-gray-600 mb-6">Are you sure you want to log out?</p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelLogout}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                No
                            </button>
                            <button
                                onClick={confirmLogout}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </div>
    );
};

export default CandidateLayout;